import React from 'react';

import BreadCrumb, { BreadCrumbOption } from 'components/BreadCrumb';
import Footer from 'components/Footer';
import Header, { ComponentProps as HeaderProps } from 'components/Header';
import Spinner from 'components/Icons/Spinner';
import Layout from 'components/Layout';
import Title from 'components/Title';
import VisuallyHidden from 'components/VisuallyHidden';
import { isMobileWebview } from 'utils/helpers';

import * as Styled from './styled';

export type ComponentProps = {
  /**
   * Page title
   */
  title?: string;
  /**
   * is the page in loading state
   *
   * @default false
   */
  loading?: boolean;
  /**
   * Optional loading content to render as part of the page loading state
   */
  loadingContent?: JSX.Element;
  /**
   * Error component to render if was provided
   */
  error?: JSX.Element;
  /**
   * Show/hide the Header element in the page
   *
   * @default true
   */
  showHeader?: boolean;
  /**
   * Show/hide the BreadCrumb element in the page
   *
   * @default true
   */
  showBreadCrumb?: boolean;
  /**
   * Show/hide the Footer element in the page
   *
   * @default false
   */
  showFooter?: boolean;
  /**
   * Show/hide the Header and Breadcrumb elements in the page whether page is shown on mobile app or not
   *
   * @default false
   */
  showSiteFurnitureInApp?: boolean;
  /**
   * the number of px padding inside the content wrapper. defaults to 32px.
   *
   * @default 32
   */
  contentPadding?: number;
  /**
   * Optional content to render always regardless of when the page is lazy loaded or not
   *
   */
  StaticContent?: JSX.Element;
  /**
   * Optional floating number between 0-1 to opacity the content between loading of the page "children" collection (defaults to '0')
   *
   * @default 0
   */
  opacityBetweenLoading?: number;
  /**
   * Optional transaction header image url
   */
  imgUrl?: string;
  /**
   * Optional, list of items for the BreadCrumbs
   *
   * @default []
   */
  breadCrumbs?: BreadCrumbOption[];
  /**
   * Optional login button props in the header. Defaults to 'undefined'
   *
   * @default {}
   */
  headerProps?: HeaderProps;
  /**
   * Optional, background color of the main section. Defaults to white
   *
   * @default ''
   */
  backgroundColor?: string;
  /**
   * Optional, background color of the Layout wrapper section. Defaults to white
   *
   * @default ''
   */
  mainBackgroundColor?: string;
  /**
   * Optional, primary background color of banner
   */
  backgroundColorBannerPrimary?: string;
  /**
   * Optional, secondary background color of banner
   */
  backgroundColorBannerSecondary?: string;

  /**
   * The page to link to for the home breadcrumb, defaults to SV homepage
   */
  homeLink?: string;
};

export const Page: React.FC<ComponentProps> = ({
  title,
  loading = false,
  showHeader = true,
  showBreadCrumb = true,
  showFooter = false,
  showSiteFurnitureInApp = false,
  contentPadding = 32,
  imgUrl,
  breadCrumbs = [],
  loadingContent,
  StaticContent,
  opacityBetweenLoading = 0,
  error,
  headerProps = {},
  backgroundColor = '',
  mainBackgroundColor = '',
  backgroundColorBannerPrimary = '',
  backgroundColorBannerSecondary = '',
  children,
  homeLink
}) => {
  const showFurniture = showSiteFurnitureInApp ? true : !isMobileWebview();

  const Fallback = (
    <Styled.Content role='main' showLoading>
      <Styled.LoadingSpinner showLoading>
        <Spinner />
        {loadingContent}
      </Styled.LoadingSpinner>
    </Styled.Content>
  );

  return (
    <Styled.Wrapper>
      <VisuallyHidden>
        <a href='#content'>Jump to main content</a>
      </VisuallyHidden>
      <Header showHeader={showHeader && showFurniture} {...headerProps} />
      <BreadCrumb
        showBreadCrumb={showBreadCrumb && showFurniture}
        items={breadCrumbs}
        noBorder={!title}
        homeLink={homeLink}
      />
      <a id='content' />
      {title && (
        <Title
          text={title}
          imgUrl={imgUrl}
          backgroundColorPrimary={backgroundColorBannerPrimary}
          backgroundColorSecondary={backgroundColorBannerSecondary}
        />
      )}
      <Layout
        contentPadding={contentPadding}
        showLoading={loading}
        showFooter={showFooter}
        backgroundColor={backgroundColor}
        mainBackgroundColor={mainBackgroundColor}
        hasTitle={!!title}>
        {error}
        {StaticContent}
        <React.Suspense fallback={Fallback}>
          <Styled.Content role='main' showLoading={loading}>
            <Styled.LoadingSpinner showLoading={loading}>
              <Spinner />
              {loadingContent}
            </Styled.LoadingSpinner>
            <Styled.ChildrenWrapper
              opacity={opacityBetweenLoading}
              hide={loading || error}
              showLoading={loading}>
              {children}
            </Styled.ChildrenWrapper>
          </Styled.Content>
        </React.Suspense>
      </Layout>
      {<Footer showFooter={showFooter && showFurniture} />}
    </Styled.Wrapper>
  );
};

export default Page;
